import { t } from '../../i18n'

/**
 * 清晰度的挑选规则与记忆。
 *
 * 规则本身抽出来是因为它有几个一眼看不出的坑：**不是每一集都有同样的档**
 * （源片短边不到 720 的那些集压根没有 720 档），所以「用户选了 720」不能直接
 * 当成一个固定的档位序号用——序号在每一集的播放列表里含义都不同。
 */

/** H5 的顶档短边。自适应只看带宽、不看解码能力，好 wifi 上的低端安卓会被推到
 *  1080 然后掉帧，而画面上只表现为卡顿。更高的档留给 App，它判断得了机型。 */
export const H5_MAX_SHORT_SIDE = 720

const STORAGE_KEY = 'drama.quality'
/** 自动挡。存成数字 0 而不是删 key，是为了和「从没选过」区分开。 */
export const AUTO = 0

export interface LevelInfo {
  /** 在 hls.levels 里的下标。切档时要用它。 */
  index: number
  /** 短边，就是用户看到的那个 360 / 540 / 720。 */
  shortSide: number
}

/** 档位按钮上那个字，例如「720P」。单位后缀走 i18n 模板（`quality.unit`）。 */
export function qualityLabel(shortSide: number): string {
  return t.quality.unit.replace('{n}', String(shortSide))
}

/** 这一集能给 H5 看的档，从低到高。超过封顶的直接不出现在选单里。 */
export function playableLevels(levels: Array<{ width: number; height: number }>): LevelInfo[] {
  return levels
    .map((lvl, index) => ({ index, shortSide: Math.min(lvl.width, lvl.height) }))
    .filter((l) => l.shortSide <= H5_MAX_SHORT_SIDE)
    .sort((a, b) => a.shortSide - b.shortSide)
}

/**
 * 按用户偏好挑一档。返回 hls 的 level 下标，-1 表示交给自适应。
 *
 * 偏好的那一档这一集没有时**往下找最接近的**，不往上：用户选 720 是想要更清楚，
 * 而这一集最高只有 540，给他 540；反过来把选了 360 的人推到 540 就是替他花流量。
 */
export function pickLevel(levels: LevelInfo[], preferred: number): number {
  if (preferred === AUTO || levels.length === 0) return -1
  const exact = levels.find((l) => l.shortSide === preferred)
  if (exact) return exact.index
  const lower = levels.filter((l) => l.shortSide < preferred)
  if (lower.length > 0) return lower[lower.length - 1]!.index
  // 比最低的那档还低（例如偏好 360 但这一集只有 540），只能给最低的
  return levels[0]!.index
}

/** 自适应时的封顶档位下标。没有可封的就返回 -1（hls 的「不封顶」）。 */
export function cappedLevel(levels: LevelInfo[], total: number): number {
  if (levels.length === 0 || levels.length === total) return -1
  return levels[levels.length - 1]!.index
}

/**
 * 读用户选过的清晰度。
 *
 * **从没选过**和**选了自动**是两回事：前者才允许我们在卡顿时主动建议降档，
 * 后者说明他明确要自适应，不该再打扰；而选了固定档的人卡了也要提醒他。
 */
export function loadPreference(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw == null) return AUTO
    const n = Number(raw)
    return Number.isFinite(n) && n >= 0 ? n : AUTO
  } catch {
    // 隐私模式下 localStorage 会抛，不能让它把播放带崩
    return AUTO
  }
}

export function savePreference(shortSide: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(shortSide))
  } catch {
    // 存不下就只在这一次播放里生效，不是错误
  }
}

/** 用户有没有亲手定过档。决定卡顿时要不要出手建议。 */
export function hasChosen(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) != null
  } catch {
    return false
  }
}
